/**
 * Small reagent-container icon for the shelf — a bottle silhouette for
 * liquids/solutions, and for solids, a shape that actually reads as what the
 * substance IS (a strip, a nail, granules, a powder heap, an irregular lump)
 * rather than one generic rock recolored — a copper strip and a fistful of
 * sulphur powder look nothing alike on a real shelf. Deliberately simple SVG,
 * not a sourced asset (Phase 3/4 split — see chemistry-lab-implplan.md).
 */
import type { DoseUnit, PhysicalState, SolidForm } from '../substanceCatalog';

interface SubstanceIconProps {
  physicalState: PhysicalState;
  color: string;
  /** What one add of it is measured in. Only 'drop' changes the drawing: a
   *  reagent dispensed a drop at a time lives in a dropper bottle, and that is
   *  worth seeing on the shelf BEFORE it is picked up — it is how a student
   *  tells the small-quantity reagents from the ones you pour. */
  unit?: DoseUnit;
  /** Only meaningful for a solid. Undefined (the common case — every plain
   *  crystalline salt) renders as a powder heap; see SolidForm's own doc. */
  form?: SolidForm;
}

/**
 * The watch glass every solid is presented in.
 *
 * A reagent on a real bench is tipped into a shallow dish, not left in a heap on
 * the counter — and a dish gives all thirty-eight solids one common base, so the
 * row reads as a shelf of samples rather than shapes floating at different
 * heights. Drawn as two arcs: the far rim behind the contents, the near rim and
 * bowl in front, so the sample sits *in* it rather than on top of it.
 */
function DishBack(): React.JSX.Element {
  return (
    <g>
      <ellipse cx="16" cy="27" rx="12" ry="3.4" fill="#000" opacity="0.18" />
      {/* Far rim — drawn before the contents so they overlap it. */}
      <path d="M4.5 24.5 A11.5 4 0 0 1 27.5 24.5" fill="none" stroke="#b9c7d6" strokeWidth="0.9" opacity="0.9" />
    </g>
  );
}

function DishFront(): React.JSX.Element {
  return (
    <g>
      {/* Bowl: shallow, glassy, sitting in front of whatever it holds. */}
      <path
        d="M4.5 24.5 A11.5 4 0 0 0 27.5 24.5 A11.5 7.5 0 0 1 4.5 24.5 Z"
        fill="rgba(214,230,243,0.55)" stroke="#aebece" strokeWidth="0.8"
      />
      <path d="M8 26.5 A7 3 0 0 0 13.5 28.4" fill="none" stroke="#fff" strokeWidth="1" opacity="0.55" strokeLinecap="round" />
    </g>
  );
}

function StripIcon({ color }: { color: string }): React.JSX.Element {
  // A thin foil strip, leant at an angle the way one rests on a shelf rather
  // than standing bolt upright.
  return (
    <g transform="rotate(-12 16 15)">
      <rect x="12.5" y="4" width="7" height="23" rx="1.4" fill={color} stroke="rgba(0,0,0,0.3)" strokeWidth="0.6" />
      <rect x="14" y="5.5" width="1.6" height="20" fill="#fff" opacity="0.3" />
    </g>
  );
}

function NailIcon({ color }: { color: string }): React.JSX.Element {
  // Flat head, a thin shaft, tapering to a point — unmistakably a nail rather
  // than a strip or a rod.
  return (
    <g transform="rotate(-10 16 16)">
      <rect x="11" y="4" width="10" height="3.2" rx="1" fill={color} stroke="rgba(0,0,0,0.3)" strokeWidth="0.6" />
      <path d="M14.6 7 H17.4 L17.4 22 L16 27 L14.6 22 Z" fill={color} stroke="rgba(0,0,0,0.3)" strokeWidth="0.6" />
      <rect x="15" y="8" width="0.9" height="12" fill="#fff" opacity="0.35" />
    </g>
  );
}

function GranulesIcon({ color }: { color: string }): React.JSX.Element {
  // A loose cluster of small irregular pebbles, not one solid mass — that's
  // what makes granules read as granules rather than a lump.
  const pebbles: [number, number, number][] = [
    [10, 20, 4.2], [17, 17, 4.8], [22, 21, 3.6], [13.5, 23.5, 3.8],
    [19.5, 24.5, 3.2], [8.5, 24, 3], [24.5, 16.5, 2.8],
  ];
  return (
    <g>
      {pebbles.map(([cx, cy, r], i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill={color} stroke="rgba(0,0,0,0.28)" strokeWidth="0.5" />
      ))}
      <circle cx="9" cy="18.5" r="1.1" fill="#fff" opacity="0.35" />
      <circle cx="16" cy="15.5" r="1.3" fill="#fff" opacity="0.35" />
    </g>
  );
}

function PowderIcon({ color }: { color: string }): React.JSX.Element {
  // A heaped mound with a soft, slightly uneven top edge — a fine powder or
  // crystalline salt tipped out of its jar, not a chiselled rock.
  return (
    <g>
      <path
        d="M6 25.5 Q7 15 11 13 Q13 10.5 16 11.5 Q19 9.5 22 12.5 Q26 14 26 25.5 Z"
        fill={color} stroke="rgba(0,0,0,0.22)" strokeWidth="0.6"
      />
      <path d="M9 15 Q13 12 17 13" stroke="#fff" strokeWidth="1" opacity="0.3" fill="none" strokeLinecap="round" />
      {/* A few scattered grains on top read as texture without needing a filter. */}
      <circle cx="12" cy="17" r="0.9" fill="#fff" opacity="0.4" />
      <circle cx="19" cy="16" r="0.8" fill="#fff" opacity="0.35" />
      <circle cx="15.5" cy="19.5" r="0.7" fill="#000" opacity="0.15" />
    </g>
  );
}

function CrystalIcon({ color }: { color: string }): React.JSX.Element {
  // Faceted grains with flat planes catching light at different angles — what
  // separates rock salt or potassium nitrate from a fine powder is precisely
  // that you can see individual facets glinting, so each crystal gets a bright
  // face and a shaded one rather than a single flat fill.
  const crystals: { d: string; hi: string }[] = [
    { d: 'M9 24 L11 16 L15.5 14.5 L17 21 L13.5 25 Z', hi: 'M11 16 L15.5 14.5 L16.2 18 L12 19 Z' },
    { d: 'M16 25 L18 17.5 L23 16.5 L24.5 23 L20 26 Z', hi: 'M18 17.5 L23 16.5 L23.6 19.5 L19 20.5 Z' },
    { d: 'M13 25.5 L14.5 20.5 L18.5 20 L19 25 Z', hi: 'M14.5 20.5 L18.5 20 L18.8 22 L15 22.6 Z' },
  ];
  return (
    <g>
      {crystals.map((c, i) => (
        <g key={i}>
          <path d={c.d} fill={color} stroke="rgba(0,0,0,0.3)" strokeWidth="0.55" strokeLinejoin="round" />
          <path d={c.hi} fill="#fff" opacity="0.42" />
        </g>
      ))}
    </g>
  );
}

function LumpIcon({ color }: { color: string }): React.JSX.Element {
  // The irregular chiselled-rock silhouette — right for a chunky, faceted
  // piece (marble chips, quicklime) that a powder heap or granule cluster
  // would misrepresent as something loose and pourable.
  return (
    <g>
      <path
        d="M7 22 L9 12 L15 8 L21 11 L25 20 L22 25 L10 25 Z"
        fill={color} stroke="rgba(0,0,0,0.25)" strokeWidth="0.6"
      />
      <path d="M9 12 L15 8 L21 11 L18 13 L12 14 Z" fill="#fff" opacity="0.28" />
    </g>
  );
}

export function SubstanceIcon({ physicalState, color, form, unit }: SubstanceIconProps): React.JSX.Element {
  if (physicalState === 'solid') {
    const Shape = form === 'strip' ? StripIcon
      : form === 'nail' ? NailIcon
        : form === 'granules' ? GranulesIcon
          : form === 'lump' ? LumpIcon
            : form === 'crystal' ? CrystalIcon
              : PowderIcon;
    // Contents are scaled down and dropped into the dish. A strip or a nail is
    // taller than it is wide, so it leans out of the bowl the way it would in
    // life; the loose forms settle inside the rim.
    const upright = form === 'strip' || form === 'nail';
    return (
      <svg className="chemlab-icon-solid" viewBox="0 0 32 32" width="36" height="36" aria-hidden="true">
        <DishBack />
        <g transform={upright ? 'translate(16 21) scale(0.74) translate(-16 -16)' : 'translate(16 22.5) scale(0.8) translate(-16 -18)'}>
          <Shape color={color} />
        </g>
        <DishFront />
      </svg>
    );
  }
  if (unit === 'drop') {
    // A dropper bottle: the same glass body, but capped with a rubber teat and
    // holding less — a reagent you are given two drops of at a time is not
    // stocked by the litre.
    return (
      <svg viewBox="0 0 26 34" width="31" height="42" aria-hidden="true">
        <ellipse cx="13" cy="31" rx="8" ry="2.3" fill="#000" opacity="0.22" />
        {/* the teat, which is what says dropper rather than bottle */}
        <path d="M10.6 6.5 Q10.6 1.6 13 1.6 Q15.4 1.6 15.4 6.5 Z" fill="#6f5b57" />
        <rect x="10.2" y="6" width="5.6" height="3.4" rx="1" fill="#8a95ab" />
        <path
          d="M10 9 Q8 11.6 8 15 V27 Q8 30 11 30 H15 Q18 30 18 27 V15 Q18 11.6 16 9 Z"
          fill="rgba(220,232,244,0.35)" stroke="#aab6c3" strokeWidth="0.7"
        />
        {/* Filled lower than a reagent bottle — the level IS the quantity. */}
        <path d="M8.4 21 H17.6 V27 Q17.6 29.3 15 29.3 H11 Q8.4 29.3 8.4 27 Z" fill={color} opacity="0.92" />
        {/* the pipette inside, drawn through the glass */}
        <rect x="12.4" y="10" width="1.2" height="13" rx="0.6" fill="#93a5b6" opacity="0.75" />
        <ellipse cx="10.8" cy="22" rx="0.9" ry="3.6" fill="#fff" opacity="0.45" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 26 34" width="31" height="42" aria-hidden="true">
      <ellipse cx="13" cy="31" rx="8.5" ry="2.4" fill="#000" opacity="0.22" />
      {/* neck + cap */}
      <rect x="10.5" y="2" width="5" height="7" rx="1" fill="#8a95ab" />
      <rect x="9.5" y="1" width="7" height="2.4" rx="1" fill="#5c6579" />
      {/* body (glass) */}
      <path
        d="M10 8 Q7.5 11 7.5 15 V27 Q7.5 30 11 30 H15 Q18.5 30 18.5 27 V15 Q18.5 11 16 8 Z"
        fill="rgba(220,232,244,0.35)" stroke="#aab6c3" strokeWidth="0.7"
      />
      {/* liquid fill */}
      <path d="M8 18 H18 V27 Q18 29.3 15 29.3 H11 Q8 29.3 8 27 Z" fill={color} opacity="0.92" />
      {/* highlight */}
      <ellipse cx="10.5" cy="20" rx="1" ry="4.5" fill="#fff" opacity="0.45" />
    </svg>
  );
}
